import React from 'react';
import styled from 'styled-components';
import { theme, toRem } from 'design';
import { ITipsBreakdownLineItem } from 'screens/MakePayment/Calculator/types';
import LineItem from './LineItem';

interface IContributingBreakdownProps {
  className?: string;
  collectedLineItems: ITipsBreakdownLineItem[];
  salesLineItems?: ITipsBreakdownLineItem[];
  contributingLineItems: ITipsBreakdownLineItem[];
  remainderLineItems?: ITipsBreakdownLineItem[];
  dataCy?: string;
}

const Header = styled.p`
  font-size: ${toRem(theme.font.size.xs)};
  font-weight: ${theme.font.weight.medium};
  color: ${theme.color.neutral[500]};
`;

const HeaderRow = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: ${toRem(theme.space.sm)};
`;

const LineItemsSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  padding: ${toRem(theme.space.lg)} 0;

  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    padding-bottom: 0;
  }

  &:not(:last-child) {
    border-bottom: 1px solid ${theme.color.neutral[300]};
  }
`;

const ContributingBreakdownLayout = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const ContributingBreakdown: React.FC<IContributingBreakdownProps> = ({ className = '', collectedLineItems, salesLineItems, contributingLineItems, remainderLineItems, dataCy = 'employee-contributing-tips-breakdown' }) => {
  const renderLineItems = (headers: string[], lineItems: ITipsBreakdownLineItem[], dataCySection: string) => (
    <LineItemsSection data-cy={`${dataCy}-${dataCySection}`}>
      <HeaderRow data-cy={`${dataCy}-${dataCySection}-header`}>
        {headers.map((header, idx) => (
          <Header key={`header_${idx}`}>{header}</Header>
        ))}
      </HeaderRow>
      {lineItems?.map((lineItem, idx) => (
        <LineItem key={`line_item_${idx}`} item={lineItem} dataCy={`${dataCy}-${dataCySection}-line-item`} />
      ))}
    </LineItemsSection>
  );

  const contributingTotal = { label: 'Total', amount: contributingLineItems?.reduce((accAmount, contribution) => accAmount + Math.round(contribution.amount), 0) || 0 };

  return (
    <ContributingBreakdownLayout className={className} data-cy={dataCy}>
      {renderLineItems(['Collected', 'Amount'], collectedLineItems, 'collected')}
      {salesLineItems?.length ? renderLineItems(['Sales', 'Amount'], salesLineItems, 'sales') : null}
      {contributingLineItems?.length ? renderLineItems(['Contributing To', 'Amount'], [...contributingLineItems, contributingTotal], 'contributed') : null}
      {remainderLineItems?.length ? renderLineItems(['Remainder', 'Amount'], remainderLineItems, 'remainder') : null}
    </ContributingBreakdownLayout>
  );
};

export default ContributingBreakdown;
